import { departmentsApi, calendarEntriesApi, companyClosuresApi, CompanyClosureEntity } from "api";
import { DepartmentEntity, CalendarEntryEntity } from "api/generated";
import { format, getDay, isSameDay, getISOWeek } from "date-fns";
import * as ExcelJS from 'exceljs';
import { t } from "i18next";
import { addLogoImageToReport, downloadReport, fetchShiftAssignmentData, getDates, reportAddTitleRow, ignoreUTC } from "utils/utils";
import { Position, ShiftData } from "./shift-assignment";
import { checkTokenRole } from "utils/checkToken";
import { isHoliday } from "utils/holidays";

// Resolves a per-day "non-working" marker (festività or chiusura aziendale) for the
// Worked Hours Summary. Returns null on ordinary days so legacy behavior is unchanged.
interface DayMarker { label: string; color: string }
const buildDayMarkerResolver = (closures: CompanyClosureEntity[]) => {
  return (date: Date): DayMarker | null => {
    // Company closure takes precedence (it's an explicit business decision).
    const closure = closures.find((c) => {
      const start = new Date(c.startDate);
      const end = new Date(c.endDate);
      start.setHours(0, 0, 0, 0); end.setHours(0, 0, 0, 0);
      const d = new Date(date); d.setHours(0, 0, 0, 0);
      return d >= start && d <= end;
    });
    if (closure) {
      return { label: closure.label || t('shift.summary.closure'), color: 'ffd8a8' };
    }
    const holiday = isHoliday(date);
    if (holiday) {
      return { label: holiday.name, color: 'd0bfff' };
    }
    return null;
  };
};

const createWorkbook = () => {
    const workbook = new ExcelJS.Workbook();
    workbook.creator = 'OneStaff';
    workbook.lastModifiedBy = 'OneStaff';
    workbook.created = new Date();
    return workbook;
}

const createWoksheet = (workbook: ExcelJS.Workbook, customTitle?: string) => {
    const worksheet = workbook.addWorksheet(customTitle ?? t('shifts.reportName'));
    worksheet.properties.defaultColWidth = 30;
    worksheet.properties.defaultRowHeight = 20;
    return worksheet;
}

const reportStyleWorksheet = (worksheet: ExcelJS.Worksheet, rowsNumber: number, columnsNumber: number) => {
    if (rowsNumber < 1 || columnsNumber < 1) return;
    // Cell colors
    for (let i = 1; i <= rowsNumber; i++) {
        if (i >= 4) {
            worksheet.getRow(i).height = 30;
        }
        for (let j = 1; j <= columnsNumber; j++) {
            const cell = worksheet.getCell(i, j);
            cell.alignment = { vertical: 'middle', horizontal: 'center' };
            cell.numFmt = '0.00'

            // check if the cell is already colored
            if (cell.fill && (cell.fill as ExcelJS.FillPattern).type === 'pattern') {
                const fillPattern = cell.fill as ExcelJS.FillPattern;
                if (fillPattern.fgColor?.argb === 'fad7a0' || fillPattern.fgColor?.argb === 'f1948a') {
                    continue;
                }
            }

            // color
            if (i % 2 === 0) {
                cell.fill = {
                    type: 'pattern',
                    pattern: 'solid',
                    fgColor: { argb: 'd6eaf8' }
                };
            } else {
                cell.fill = {
                    type: 'pattern',
                    pattern: 'solid',
                    fgColor: { argb: 'aed6f1' }
                };
            }
        }
    }
}

const addHeaderRows = (
    worksheet: ExcelJS.Worksheet,
    department: DepartmentEntity,
    workPositions: Array<{ id: number; name: string; }>,
    workPositionMaxDimension: Record<number, number>
) => {
    const departmentsRow = worksheet.getRow(2);
    const workPositionsRow = worksheet.getRow(3);

    departmentsRow.height = 25;
    departmentsRow.font =
    {
        name: 'Arial',
        family: 2,
        size: 12,
        bold: true,
    };

    workPositionsRow.height = 25;
    workPositionsRow.font =
    {
        name: 'Arial',
        family: 2,
        size: 12,
        bold: true,
    };

    const depCell = departmentsRow.getCell(1);
    depCell.value = department.name;

    let depSize = 0;
    for (let i = 0; i < workPositions.length; i++) {
        const workPosition = workPositions[i];
        const maxDim = workPositionMaxDimension[workPosition.id];
        for (let j = 0; j < maxDim; j++) {
            workPositionsRow.getCell(3 + depSize).value = workPositions[i].name + (maxDim > 1 ? ' ' + (j + 1) : '');
            depSize += 1;
        }
    }

    departmentsRow.getCell(2 + depSize).merge(depCell);

    return depSize;
}

const addDatesRow = (worksheet: ExcelJS.Worksheet, dates: Date[], shifts: ShiftData[], resources: Record<number, Position[]>) => {
    let rows = 4;
    const weekdays = ["SUNDAY", "MONDAY", "TUESDAY", "WEDNESDAY", "THURSDAY", "FRIDAY", "SATURDAY"];
    for (const date of dates) {
        const dateRow = worksheet.getRow(rows);
        dateRow.getCell(1).value = t('employees.agreements.day.' + weekdays[getDay(date)]) + '\n' + format(date, 'dd/MM/yyyy') + ' \u00b7 ' + t('shift.weekShort', { n: getISOWeek(date) });
        const dateShifts = shifts.filter((shift) => { return shift.date ? isSameDay(new Date(shift.date), date) : false });
        const shiftsNumber = dateShifts.length;
        for (let i = 0; i < shiftsNumber; i++) {
            const shift = dateShifts[i];
            const shiftResources = resources[shift.index];
            const shiftRow = worksheet.getRow(rows + i)
            shiftRow.getCell(2).value = shift.name + '\n' + shift.startHour + ' - ' + shift.endHour;
            for (let i = 0; i < shiftResources.length; i++) {
                const res = shiftResources[i];

                let customHours = '';
                if (res.start && res.end) {
                    customHours = '\n' + format(ignoreUTC(res.start), 'HH:mm') + ' - ' + format(ignoreUTC(res.end), 'HH:mm');
                } else if (res.start) {
                    customHours = '\n' + t('shift.startTime') + ': ' + format(ignoreUTC(res.start), 'HH:mm');
                } else if (res.end) {
                    customHours = '\n' + t('shift.endTime') + ': ' + format(ignoreUTC(res.end), 'HH:mm');
                }

                // Build break information string
                let breakInfo = '';
                // Main break
                if (res.breakStartTime && res.breakEndTime) {
                    breakInfo += '\n' + t('shift.break') + ': ' + format(ignoreUTC(res.breakStartTime), 'HH:mm') + ' - ' + format(ignoreUTC(res.breakEndTime), 'HH:mm');
                }
                // Additional breaks
                if (res.additionalBreaks && res.additionalBreaks.length > 0) {
                    res.additionalBreaks.forEach((ab, index) => {
                        breakInfo += '\n' + t('shift.additionalBreak') + ' #' + (index + 1) + ': ' + format(ignoreUTC(ab.startTime), 'HH:mm') + ' - ' + format(ignoreUTC(ab.endTime), 'HH:mm');
                    });
                }

                const assignmentCell = shiftRow.getCell(3 + i);

                if (res.firstName && res.lastName) {
                    assignmentCell.value = res.firstName + ' ' + res.lastName + (customHours ? ' ' + customHours : '') + (breakInfo || '');
                    if (!res.published) assignmentCell.fill = {
                        type: 'pattern',
                        pattern: 'solid',
                        fgColor: { argb: 'fad7a0' }
                    };
                    if (res.deleted) assignmentCell.fill = {
                        type: 'pattern',
                        pattern: 'solid',
                        fgColor: { argb: 'f1948a' }
                    };
                }
            }
        }
        if (shiftsNumber > 1) worksheet.getRow(rows + shiftsNumber - 1).getCell(1).merge(dateRow.getCell(1));
        rows = rows + (shiftsNumber > 0 ? shiftsNumber : 1);
    }
    return rows;
}

const addDepartmentTable = async (workbook: ExcelJS.Workbook, department: DepartmentEntity, seasonId: number, dates: Date[], selectedWorkPositions?: string[]) => {
    const { workPositionMaxDimension, shifts, workPositions, resources } = await fetchShiftAssignmentData(department, seasonId, dates[0], dates[dates.length - 1], true)

    if (shifts.length === 0 || workPositions.length === 0) return;

    // Filter work positions if specified
    let filteredWorkPositions = workPositions;
    let filteredWorkPositionMaxDimension = workPositionMaxDimension;
    
    if (selectedWorkPositions && selectedWorkPositions.length > 0) {
        const selectedWorkPositionIds = selectedWorkPositions.map(id => parseInt(id));
        filteredWorkPositions = workPositions.filter(wp => selectedWorkPositionIds.includes(wp.id));
        
        // Update the max dimension object to only include selected work positions
        filteredWorkPositionMaxDimension = {};
        filteredWorkPositions.forEach(wp => {
            if (workPositionMaxDimension[wp.id]) {
                filteredWorkPositionMaxDimension[wp.id] = workPositionMaxDimension[wp.id];
            }
        });
    }

    const worksheet = createWoksheet(workbook, department.name);

    const columnsNumber = addHeaderRows(worksheet, department, filteredWorkPositions, filteredWorkPositionMaxDimension) + 2;

    const rowsNumber = addDatesRow(worksheet, dates, shifts, resources) - 1;

    reportAddTitleRow(worksheet, columnsNumber + 1, t('shifts.reportName'));

    reportStyleWorksheet(worksheet, rowsNumber, columnsNumber);

    await addLogoImageToReport(worksheet, workbook);
}

const addWorkedHoursSummaryTab = async (workbook: ExcelJS.Workbook, startDate: Date, endDate: Date, seasonId: number, departmentIds?: number[]) => {
    const { shiftApi, departmentsEmployeesApi, calendarEntriesApi } = await import('api');
    const i18nMod = await import('i18next');
    const t = i18nMod.t;
    const lang: string = ((i18nMod.default as any)?.language ?? 'en').slice(0, 2);

    // Planned/worked minutes. NOTE: this endpoint returns every employee that has shifts in
    // the period regardless of department, so we scope by employee membership below.
    const dailyData: any[] = (await shiftApi.hoursForPeriodDaily(startDate.toISOString(), endDate.toISOString(), seasonId)).data;

    // Festività + chiusura aziendale awareness (additive: no marker => unchanged output)
    let closures: CompanyClosureEntity[] = [];
    try {
        closures = (await companyClosuresApi.findAll({
            from: startDate.toISOString(),
            to: endDate.toISOString(),
            seasonId,
        })).data ?? [];
    } catch (e) {
        // closures are optional; never block the report
        closures = [];
    }
    const resolveDayMarker = buildDayMarkerResolver(closures);

    // Department roster: used only to (a) scope shift-employees to the selected department(s)
    // and (b) resolve employee names for absence entries — NOT to add idle employees as columns.
    const rosterById = new Map<number, any>();
    let allowedIds: Set<number> | null = null;
    if (departmentIds && departmentIds.length > 0) {
        const rosterResults = await Promise.all(
            departmentIds.map(async (id) => departmentsEmployeesApi.findAll(String(id), 'false', seasonId))
        );
        rosterResults.forEach(r => (r.data ?? []).forEach((rel: any) => {
            if (rel.employee) rosterById.set(rel.employee.id, rel.employee);
        }));
        if (rosterById.size > 0) allowedIds = new Set(rosterById.keys());
    }

    // --- Absences: employeeId -> date string -> { label, hours } (dept-scoped) ---
    const empDayAbsence: Record<number, Record<string, { label: string; hours: number }>> = {};
    const absenceEmployeeIds = new Set<number>();
    try {
        // findByDateRange REQUIRES employeeIds (the DTO field is not optional). Pass the
        // department roster + anyone with shifts; the departmentId still scopes the result.
        const reportEmployeeIds = Array.from(new Set<number>([
            ...Array.from(rosterById.keys()),
            ...dailyData.map((d: any) => d.employee.id),
        ]));
        const absResults = departmentIds && departmentIds.length > 0 && reportEmployeeIds.length > 0
            ? await Promise.all(departmentIds.map(async (id) => calendarEntriesApi.findByDateRange({
                employeeIds: reportEmployeeIds,
                startDate: startDate.toISOString(),
                endDate: endDate.toISOString(),
                seasonId,
                departmentId: id,
            } as any)))
            : [];
        const absEntries = absResults.flatMap(r => r.data ?? []);
        const absenceLabel = (e: any): string => {
            const at = e.absenceType;
            if (at) return lang === 'it' ? at.labelIt : lang === 'de' ? at.labelDe : lang === 'fr' ? at.labelFr : at.labelEn;
            if (e.type === 'CUSTOM' && e.customType) return e.customType.name;
            return t(`employees.calendar.types.${String(e.type ?? '').toLowerCase()}`) || e.type;
        };
        absEntries.forEach((e: any) => {
            if (!e.accepted) return;
            absenceEmployeeIds.add(e.employeeId);
            // Hours: timed entry => interval; half day => 4h; all-day => 8h (kept simple/consistent).
            let hours = 8;
            if (e.startTime && e.endTime) {
                const [sh, sm] = e.startTime.split(':').map(Number);
                const [eh, em] = e.endTime.split(':').map(Number);
                hours = Math.max(0, (eh * 60 + em - (sh * 60 + sm)) / 60);
            } else if (e.type === 'HALF_DAY') {
                hours = 4;
            }
            const label = absenceLabel(e);
            // Key by the UTC date (matches how worked-hours/day cells are keyed). Do NOT use
            // setHours(0,0,0,0) here — that shifts the date by the local tz offset.
            const start = new Date(e.startedOn);
            const end = new Date(e.endedOn);
            for (let d = new Date(start); d <= end; d = new Date(d.getTime() + 24 * 60 * 60 * 1000)) {
                const k = d.toISOString().slice(0, 10);
                if (!empDayAbsence[e.employeeId]) empDayAbsence[e.employeeId] = {};
                empDayAbsence[e.employeeId][k] = { label, hours };
            }
        });
    } catch (e) {
        // absences are optional enrichment; never block the report
    }

    // Employee columns = employees with >=1 shift (dept-scoped) OR >=1 absence in the period.
    // Idle employees (no shift AND no event) are intentionally excluded.
    const employeesMap = new Map<number, any>();
    dailyData.forEach(d => {
        if (allowedIds && !allowedIds.has(d.employee.id)) return;
        employeesMap.set(d.employee.id, d.employee);
    });
    absenceEmployeeIds.forEach(id => {
        if (employeesMap.has(id)) return;
        const emp = rosterById.get(id) ?? dailyData.find(d => d.employee.id === id)?.employee;
        if (emp) employeesMap.set(id, emp);
    });
    const employees = Array.from(employeesMap.values());

    // Get all unique dates in the period (rows)
    const allDates: Date[] = [];
    for (let d = new Date(startDate); d <= endDate; d = new Date(d.getTime() + 24 * 60 * 60 * 1000)) {
        allDates.push(new Date(d));
    }

    // Group dates by calendar week (ISO week)
    const weeks: Record<number, Date[]> = {};
    allDates.forEach(date => {
        const week = getISOWeek(date);
        if (!weeks[week]) weeks[week] = [];
        weeks[week].push(date);
    });
    const weekNumbers = Object.keys(weeks).map(Number).sort((a, b) => a - b);

    // Build a lookup: employeeId -> date string -> minutes (only for shown employees).
    const empDayMinutes: Record<number, Record<string, number>> = {};
    dailyData.forEach(d => {
        if (!employeesMap.has(d.employee.id)) return;
        if (!empDayMinutes[d.employee.id]) empDayMinutes[d.employee.id] = {};
        const key = d.date.slice(0, 10);
        empDayMinutes[d.employee.id][key] = (empDayMinutes[d.employee.id][key] ?? 0) + d.totalMinutes;
    });

    // Create worksheet
    const worksheet = workbook.addWorksheet(t('shift.summary.tabName') || 'Worked Hours Summary');
    worksheet.properties.defaultColWidth = 16;
    worksheet.properties.defaultRowHeight = 22;

    // Set columns: first column wider, employee columns wider, only as many as needed
    worksheet.columns = [
        { width: 26 },
        ...employees.map(() => ({ width: 19 }))
    ];
    const totalCols = employees.length + 1;

    // --- Modern Heading and Logo ---
    const heading = (t('shift.summary.heading') && t('shift.summary.heading') !== 'shift.summary.heading') ? t('shift.summary.heading') : (t('shift.summary.tabName') && t('shift.summary.tabName') !== 'shift.summary.tabName' ? t('shift.summary.tabName') : 'Worked Hours Overview');
    worksheet.addRow([]); // Row 1 (for logo)
    worksheet.addRow([heading]); // Row 2 (heading)
    worksheet.mergeCells(2, 1, 2, totalCols);
    worksheet.getRow(2).font = { bold: true, size: 20, color: { argb: 'FFFFFFFF' } };
    worksheet.getRow(2).alignment = { vertical: 'middle', horizontal: 'center' };
    worksheet.getRow(2).height = 36;
    worksheet.getRow(2).fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: '667eea' },
    };
    // Add logo (top left)
    await addLogoImageToReport(worksheet, workbook);
    // Modern accent line (Row 3)
    worksheet.addRow([]);
    worksheet.mergeCells(3, 1, 3, totalCols);
    worksheet.getRow(3).height = 6;
    worksheet.getRow(3).fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: '764ba2' },
    };
    // Empty row for spacing (Row 4)
    worksheet.addRow([]);

    // Header row: first cell = date label, then one per employee (first name, then last name, split by \n)
    const header = [(t('shift.summary.date') && t('shift.summary.date') !== 'shift.summary.date') ? t('shift.summary.date') : 'Date'];
    employees.forEach(emp => header.push(emp.firstName + '\n' + emp.lastName));
    worksheet.addRow(header);

    // Style header
    worksheet.getRow(5).font = { bold: true, size: 13, color: { argb: 'FFFFFFFF' } };
    worksheet.getRow(5).alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };
    worksheet.getRow(5).height = 38;
    worksheet.getRow(5).fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: '667eea' },
    };
    worksheet.getRow(5).border = {
        bottom: { style: 'medium', color: { argb: '764ba2' } },
    };
    worksheet.columns.forEach(col => {
        col.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };
    });

    // Data rows: for each week, for each day, then KW total, then final total
    let rowIdx = 6;
    const weekTotalRows: number[] = [];
    // Track day row numbers for each employee for correct SUM formulas
    const employeeDayRows: number[][] = employees.map(() => []);
    weekNumbers.forEach((weekNo, weekIdx) => {
        const weekDates = weeks[weekNo];
        const weekDayRows: number[] = [];
        weekDates.forEach((date, dayIdx) => {
            const weekday = ["SUNDAY", "MONDAY", "TUESDAY", "WEDNESDAY", "THURSDAY", "FRIDAY", "SATURDAY"][date.getDay()];
            const marker = resolveDayMarker(date);
            const dateLabel = t('employees.agreements.day.' + weekday) + ' ' + format(date, 'dd/MM')
                + (marker ? ` · ${marker.label}` : '');
            const row = [dateLabel];
            employees.forEach((emp, empIdx) => {
                row.push(''); // placeholder, will set value below
            });
            const addedRow = worksheet.addRow(row);
            // Set Excel time and format for employee columns
            const dateKey = date.toISOString().slice(0, 10);
            employees.forEach((emp, empIdx) => {
                const min = empDayMinutes[emp.id]?.[dateKey] || 0;
                const absence = empDayAbsence[emp.id]?.[dateKey];
                const cell = addedRow.getCell(empIdx + 2);

                // If the employee didn't work but has a justified absence, show the causale + hours
                // instead of an empty 0:00 cell. SUM ignores text cells, so weekly totals stay correct.
                if (min === 0 && absence) {
                    cell.value = `${absence.label} (${absence.hours}h)`;
                    cell.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };
                    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'c5e1a5' } }; // absence = soft green
                    return;
                }

                const cellValue = min > 0 ? min / 60 / 24 : 0;
                cell.value = cellValue;
                cell.numFmt = 'hh:mm';
                // Conditional coloring based on Italian work law
                const hours = min / 60;
                let fillColor = null;
                if (hours > 13) {
                    fillColor = { argb: 'ff4d4f' }; // Red for over legal limit
                } else if (hours >= 11) {
                    fillColor = { argb: 'ffa940' }; // Orange for 11+ hours
                } else if (hours > 0 && hours < 6 && !marker) {
                    // Skip the "under 6h" flag on festività / chiusura — no work is expected.
                    fillColor = { argb: 'fff566' }; // Yellow for less than 6 hours
                }
                if (fillColor) {
                    cell.fill = {
                        type: 'pattern',
                        pattern: 'solid',
                        fgColor: fillColor,
                    };
                }
            });
            // Style alternating day rows
            const thisRow = worksheet.getRow(rowIdx);
            thisRow.font = { size: 12 };
            thisRow.border = {
                top: { style: 'thin', color: { argb: 'CCCCCC' } },
                bottom: { style: 'thin', color: { argb: 'CCCCCC' } },
            };
            // Only color the used cells (date + employees)
            for (let colIdx = 1; colIdx <= employees.length + 1; colIdx++) {
                const cell = thisRow.getCell(colIdx);
                // Only apply default fill if not already set by legal check
                if (!cell.fill) {
                    cell.fill = {
                        type: 'pattern',
                        pattern: 'solid',
                        fgColor: { argb: rowIdx % 2 === 0 ? 'f8fafc' : 'e9ecef' },
                    };
                }
                // Set numFmt for employee columns (skip date col)
                if (colIdx > 1) {
                    cell.numFmt = 'hh:mm';
                }
            }
            // Tint the date label on festività / chiusura aziendale days
            if (marker) {
                const dateCell = thisRow.getCell(1);
                dateCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: marker.color } };
                dateCell.font = { size: 12, bold: true, italic: true };
            }
            // Track this day row for each employee
            employees.forEach((_, empIdx) => {
                employeeDayRows[empIdx].push(rowIdx);
            });
            weekDayRows.push(rowIdx);
            rowIdx++;
        });
        // KW total row
        const kwLabel = (t('shift.summary.kw', { n: weekNo }) && t('shift.summary.kw', { n: weekNo }) !== 'shift.summary.kw') ? t('shift.summary.kw', { n: weekNo }) : `KW ${weekNo}`;
        const kwRow = [kwLabel];
        employees.forEach(() => kwRow.push(''));
        const addedRow = worksheet.addRow(kwRow);
        // Set formulas for KW total row (only day rows of this week)
        employees.forEach((emp, empIdx) => {
            const colLetter = String.fromCharCode('B'.charCodeAt(0) + empIdx);
            const sumRows = weekDayRows.map(r => `${colLetter}${r}`);
            addedRow.getCell(empIdx + 2).value = { formula: `SUM(${sumRows.join(',')})` };
            addedRow.getCell(empIdx + 2).numFmt = '[h]:mm';
        });
        // Style KW total row
        addedRow.font = { bold: true, size: 12, color: { argb: 'FFFFFFFF' } };
        addedRow.fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: '764ba2' },
        };
        addedRow.border = {
            top: { style: 'medium', color: { argb: '667eea' } },
            bottom: { style: 'medium', color: { argb: '667eea' } },
        };
        weekTotalRows.push(rowIdx);
        rowIdx++;
    });
    // Final total row
    const totalLabel = (t('shift.summary.total') && t('shift.summary.total') !== 'shift.summary.total') ? t('shift.summary.total') : 'Total';
    const totalRow = [totalLabel];
    employees.forEach(() => totalRow.push(''));
    const addedTotalRow = worksheet.addRow(totalRow);
    employees.forEach((emp, empIdx) => {
        const colLetter = String.fromCharCode('B'.charCodeAt(0) + empIdx);
        const sumRows = employeeDayRows[empIdx].map(r => `${colLetter}${r}`);
        addedTotalRow.getCell(empIdx + 2).value = { formula: `SUM(${sumRows.join(',')})` };
        addedTotalRow.getCell(empIdx + 2).numFmt = '[h]:mm';
    });
    // Style total row
    addedTotalRow.font = { bold: true, size: 13, color: { argb: 'FFFFFFFF' } };
    addedTotalRow.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: '667eea' },
    };
    addedTotalRow.border = {
        top: { style: 'thick', color: { argb: '764ba2' } },
        bottom: { style: 'thick', color: { argb: '764ba2' } },
    };

    // --- Monthly summaries (worked hours + event hours) for each fully-elapsed month ---
    // A month is included only when the selection contains its LAST calendar day, e.g.
    // 20.06–10.08 => June (20–30) and full July, but NOT the partial August.
    const monthDatesMap: Record<string, string[]> = {};
    allDates.forEach(d => {
        const key = d.toISOString().slice(0, 10);
        const mk = key.slice(0, 7);
        (monthDatesMap[mk] = monthDatesMap[mk] ?? []).push(key);
    });
    const qualifyingMonths = Object.keys(monthDatesMap).sort().filter(mk => {
        const [y, m] = mk.split('-').map(Number);
        const daysInMonth = new Date(Date.UTC(y, m, 0)).getUTCDate();
        const maxDayPresent = Math.max(...monthDatesMap[mk].map(k => Number(k.slice(8, 10))));
        return maxDayPresent === daysInMonth;
    });
    const fmtMonth = (mk: string) => {
        const [y, m] = mk.split('-').map(Number);
        return new Date(Date.UTC(y, m - 1, 1)).toLocaleDateString(lang, { month: 'long', year: 'numeric', timeZone: 'UTC' });
    };

    if (qualifyingMonths.length > 0) {
        worksheet.addRow([]);
        const hdr = worksheet.addRow([t('shift.summary.monthlyTitle')]);
        worksheet.mergeCells(hdr.number, 1, hdr.number, totalCols);
        hdr.font = { bold: true, size: 13, color: { argb: 'FFFFFFFF' } };
        hdr.alignment = { vertical: 'middle', horizontal: 'center' };
        hdr.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: '764ba2' } };
        hdr.height = 26;

        qualifyingMonths.forEach(mk => {
            const keys = monthDatesMap[mk];
            const monthName = fmtMonth(mk);

            const workedRow = worksheet.addRow([`${monthName} · ${t('shift.summary.worked')}`, ...employees.map(() => '')]);
            employees.forEach((emp, empIdx) => {
                const minutes = keys.reduce((s, k) => s + (empDayMinutes[emp.id]?.[k] ?? 0), 0);
                const c = workedRow.getCell(empIdx + 2);
                c.value = minutes / 60 / 24;
                c.numFmt = '[h]:mm';
            });
            workedRow.font = { bold: true, size: 12 };
            workedRow.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'e9ecef' } };

            // One absence row per distinct causale present in this month.
            const monthLabels = new Set<string>();
            employees.forEach(emp => keys.forEach(k => {
                const a = empDayAbsence[emp.id]?.[k];
                if (a) monthLabels.add(a.label);
            }));
            Array.from(monthLabels).sort().forEach(label => {
                const row = worksheet.addRow([`${monthName} · ${label}`, ...employees.map(() => '')]);
                employees.forEach((emp, empIdx) => {
                    const hrs = keys.reduce((s, k) => {
                        const a = empDayAbsence[emp.id]?.[k];
                        return s + (a && a.label === label ? a.hours : 0);
                    }, 0);
                    const c = row.getCell(empIdx + 2);
                    c.value = hrs > 0 ? `${Math.round(hrs * 10) / 10}h` : '';
                    c.alignment = { horizontal: 'center' };
                });
                row.font = { size: 12, italic: true };
                row.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'c5e1a5' } };
            });
        });
    }

    // --- Total absence hours per employee across the whole selection, grouped by causale ---
    const allLabels = new Set<string>();
    employees.forEach(emp => Object.values(empDayAbsence[emp.id] ?? {}).forEach((a: any) => { if (a) allLabels.add(a.label); }));

    const totalHeader = worksheet.addRow([t('shift.summary.totalEvents'), ...employees.map(() => '')]);
    worksheet.mergeCells(totalHeader.number, 1, totalHeader.number, totalCols);
    totalHeader.font = { bold: true, size: 13, color: { argb: 'FFFFFFFF' } };
    totalHeader.alignment = { vertical: 'middle', horizontal: 'center' };
    totalHeader.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: '2f9e44' } };
    totalHeader.height = 24;

    Array.from(allLabels).sort().forEach(label => {
        const row = worksheet.addRow([label, ...employees.map(() => '')]);
        employees.forEach((emp, empIdx) => {
            const hrs = Object.values(empDayAbsence[emp.id] ?? {}).reduce(
                (s: number, a: any) => s + (a && a.label === label ? a.hours : 0), 0,
            );
            const c = row.getCell(empIdx + 2);
            c.value = hrs > 0 ? `${Math.round(hrs * 10) / 10}h` : '';
            c.alignment = { horizontal: 'center' };
        });
        row.font = { bold: true, size: 12 };
        row.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'd3f9d8' } };
    });

    // Grand total absence hours per employee (sum across all causali)
    const absenceTotalRow = worksheet.addRow([t('shift.summary.total'), ...employees.map(() => '')]);
    employees.forEach((emp, empIdx) => {
        const hrs = Object.values(empDayAbsence[emp.id] ?? {}).reduce((s: number, a: any) => s + (a?.hours ?? 0), 0);
        const c = absenceTotalRow.getCell(empIdx + 2);
        c.value = hrs > 0 ? `${Math.round(hrs * 10) / 10}h` : '';
        c.alignment = { horizontal: 'center' };
    });
    absenceTotalRow.font = { bold: true, size: 13, color: { argb: 'FFFFFFFF' } };
    absenceTotalRow.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: '2f9e44' } };
    absenceTotalRow.border = {
        top: { style: 'thick', color: { argb: '764ba2' } },
        bottom: { style: 'thick', color: { argb: '764ba2' } },
    };

    // Legend explaining the day markers (festività / chiusura aziendale)
    worksheet.addRow([]);
    const legendItems: Array<{ color: string; label: string }> = [
        { color: 'c5e1a5', label: t('shift.summary.legendAbsence') },
        { color: 'd0bfff', label: t('shift.summary.legendHoliday') },
        { color: 'ffd8a8', label: t('shift.summary.legendClosure') },
        { color: 'fff566', label: t('shift.summary.legendUnder6') },
        { color: 'ffa940', label: t('shift.summary.legendOver11') },
        { color: 'ff4d4f', label: t('shift.summary.legendOver13') },
    ];
    legendItems.forEach((item) => {
        const r = worksheet.addRow(['', item.label]);
        const swatch = r.getCell(1);
        swatch.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: item.color } };
        swatch.border = { top: { style: 'thin', color: { argb: 'CCCCCC' } }, bottom: { style: 'thin', color: { argb: 'CCCCCC' } }, left: { style: 'thin', color: { argb: 'CCCCCC' } }, right: { style: 'thin', color: { argb: 'CCCCCC' } } };
        r.getCell(2).font = { size: 11, italic: true };
        r.getCell(2).alignment = { horizontal: 'left' };
    });
};

const addCompactShiftOverviewTab = async (workbook: ExcelJS.Workbook, startDate: Date, endDate: Date, seasonId: number, selectedDepartments?: string[], selectedWorkPositions?: string[]) => {
    const { t } = await import('i18next');
    
    // Get all departments to process
    const role = checkTokenRole();
    const allDepartments = role === "DEPCHIEF" ?
        (await departmentsApi.findWhereIsBoss(seasonId)).data :
        (await departmentsApi.findAll()).data;
    
    // Filter departments if specified
    let departmentsToProcess = allDepartments;
    if (selectedDepartments && selectedDepartments.length > 0) {
        departmentsToProcess = allDepartments.filter(dept => selectedDepartments.includes(dept.id.toString()));
    }

    // Get all dates in the range
    const allDates: Date[] = [];
    for (let d = new Date(startDate); d <= endDate; d = new Date(d.getTime() + 24 * 60 * 60 * 1000)) {
        allDates.push(new Date(d));
    }

    // Group dates by calendar week (ISO week)
    
    const weeks: Record<number, Date[]> = {};
    allDates.forEach(date => {
        const week = getISOWeek(date);
        if (!weeks[week]) weeks[week] = [];
        weeks[week].push(date);
    });
    const weekNumbers = Object.keys(weeks).map(Number).sort((a, b) => a - b);

    // Create a separate tab for each department
    for (const department of departmentsToProcess) {
        const { shifts, workPositions, resources } = await fetchShiftAssignmentData(department, seasonId, startDate, endDate, true);
        
        // Filter work positions if specified
        let filteredWorkPositions = workPositions;
        if (selectedWorkPositions && selectedWorkPositions.length > 0) {
            const selectedWorkPositionIds = selectedWorkPositions.map(id => parseInt(id));
            filteredWorkPositions = workPositions.filter(wp => selectedWorkPositionIds.includes(wp.id));
        }

        // Collect work positions and their employees for each day
        const workPositionData: Record<string, Record<string, string[]>> = {};
        
        // Get calendar entries for this department to check employee availability
        const calendarEntries: CalendarEntryEntity[] = [];
        try {
            const calendarResponse = await calendarEntriesApi.findByDate(
                seasonId,
                startDate.toISOString(),
                department.id
            );
            calendarEntries.push(...calendarResponse.data);
        } catch (error) {
            console.error('Error fetching calendar entries:', error);
        }
        
        // Process each work position
        for (const workPosition of filteredWorkPositions) {
            const workPositionKey = workPosition.name;
            if (!workPositionData[workPositionKey]) {
                workPositionData[workPositionKey] = {};
            }

            // Process each date
            for (const date of allDates) {
                const dateKey = date.toISOString().slice(0, 10);
                if (!workPositionData[workPositionKey][dateKey]) {
                    workPositionData[workPositionKey][dateKey] = [];
                }

                // Find shifts for this date and work position
                const dateShifts = shifts.filter(shift => 
                    shift.date ? isSameDay(new Date(shift.date), date) : false
                );

                // Collect employees for this work position on this date
                for (const shift of dateShifts) {
                    const shiftResources = resources[shift.index];
                    for (const resource of shiftResources) {
                        if (resource.workPositionId === workPosition.id && resource.firstName && resource.lastName) {
                            const employeeName = `${resource.firstName} ${resource.lastName}`;
                            if (!workPositionData[workPositionKey][dateKey].includes(employeeName)) {
                                workPositionData[workPositionKey][dateKey].push(employeeName);
                            }
                        }
                    }
                }
            }
        }

        // Filter out work positions that have no employees assigned on any day
        const workPositionKeys = Object.keys(workPositionData).filter(workPositionKey => {
            const hasEmployees = Object.values(workPositionData[workPositionKey]).some(employees => employees.length > 0);
            return hasEmployees;
        }).sort();

        // Create worksheet for this department
        const worksheet = workbook.addWorksheet(`${department.name} - ${t('shift.overview.tabName') || 'Shift Overview'}`);
        worksheet.properties.defaultColWidth = 25;
        worksheet.properties.defaultRowHeight = 25;

        // --- Modern Heading and Logo ---
        const heading = `${department.name} - ${t('shift.overview.heading') || 'Shift Overview'}`;
        worksheet.addRow([]); // Row 1 (for logo)
        worksheet.addRow([heading]); // Row 2 (heading)
        worksheet.mergeCells(2, 1, 2, 50); // Merge enough columns for the heading
        worksheet.getRow(2).font = { bold: true, size: 20, color: { argb: 'FFFFFFFF' } };
        worksheet.getRow(2).alignment = { vertical: 'middle', horizontal: 'center' };
        worksheet.getRow(2).height = 36;
        worksheet.getRow(2).fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: '667eea' },
        };
        
        // Add logo (top left)
        await addLogoImageToReport(worksheet, workbook, 1, 1);
        
        // Modern accent line (Row 3)
        worksheet.addRow([]);
        worksheet.mergeCells(3, 1, 3, 50);
        worksheet.getRow(3).height = 6;
        worksheet.getRow(3).fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: '764ba2' },
        };
        
        // Empty row for spacing (Row 4)
        worksheet.addRow([]);

        let currentRow = 5;
        for (const weekNo of weekNumbers) {
            const weekDates = weeks[weekNo];
            
            // Week header
            const kwLabel = t('shift.summary.kw', { n: weekNo }) || `KW ${weekNo}`;
            const weekHeader = [kwLabel];
            weekDates.forEach(date => {
                const weekday = ["SUNDAY", "MONDAY", "TUESDAY", "WEDNESDAY", "THURSDAY", "FRIDAY", "SATURDAY"][date.getDay()];
                const dayLabel = `${t('employees.agreements.day.' + weekday)} ${format(date, 'dd/MM')}`;
                weekHeader.push(dayLabel);
            });
            
            const weekHeaderRow = worksheet.addRow(weekHeader);
            weekHeaderRow.font = { bold: true, size: 14, color: { argb: 'FFFFFFFF' } };
            weekHeaderRow.fill = {
                type: 'pattern',
                pattern: 'solid',
                fgColor: { argb: '764ba2' },
            };
            weekHeaderRow.height = 30;
            weekHeaderRow.alignment = { vertical: 'middle', horizontal: 'center' };
            currentRow++;

            // Work position rows for this week
            for (const workPositionKey of workPositionKeys) {
                const workPositionRow = [workPositionKey];
                
                for (const date of weekDates) {
                    const dateKey = date.toISOString().slice(0, 10);
                    const employees = workPositionData[workPositionKey][dateKey] || [];
                    
                    // Check for unavailable employees due to calendar events
                    const unavailableEmployees: string[] = [];
                    for (const entry of calendarEntries) {
                        const entryStart = new Date(entry.startedOn);
                        const entryEnd = new Date(entry.endedOn);
                        const currentDate = new Date(date);
                        
                        // Check if the current date falls within the calendar entry period
                        if (currentDate >= entryStart && currentDate <= entryEnd) {
                            // Find the employee name from the calendar entry
                            const employeeName = entry.employeeToSeason?.employee?.firstName && entry.employeeToSeason?.employee?.lastName
                                ? `${entry.employeeToSeason.employee.firstName} ${entry.employeeToSeason.employee.lastName}`
                                : null;
                            
                            // Check if this employee is assigned to this work position on this date
                            if (employeeName && employees.includes(employeeName)) {
                                unavailableEmployees.push(employeeName);
                            }
                        }
                    }
                    
                    // Create the cell content with unavailable employees marked
                    let cellContent = employees.join('\n');
                    if (unavailableEmployees.length > 0) {
                        cellContent += '\n' + unavailableEmployees.map(emp => `🚫 ${emp}`).join('\n');
                    }
                    
                    workPositionRow.push(cellContent);
                }
                
                const addedRow = worksheet.addRow(workPositionRow);
                addedRow.font = { size: 11 };
                addedRow.alignment = { vertical: 'middle', horizontal: 'left', wrapText: true };
                
                // Calculate height based on the maximum number of employees in any cell for this row
                const maxEmployeesInRow = workPositionRow.slice(1).reduce((max, cell) => {
                    const employeeCount = cell ? cell.split('\n').length : 0;
                    return Math.max(max, employeeCount);
                }, 0);
                addedRow.height = Math.max(25, maxEmployeesInRow * 15);
                
                // Style alternating rows
                if (currentRow % 2 === 0) {
                    addedRow.fill = {
                        type: 'pattern',
                        pattern: 'solid',
                        fgColor: { argb: 'f8fafc' },
                    };
                } else {
                    addedRow.fill = {
                        type: 'pattern',
                        pattern: 'solid',
                        fgColor: { argb: 'e9ecef' },
                    };
                }
                
                // Add borders
                addedRow.border = {
                    top: { style: 'thin', color: { argb: 'CCCCCC' } },
                    bottom: { style: 'thin', color: { argb: 'CCCCCC' } },
                    left: { style: 'thin', color: { argb: 'CCCCCC' } },
                    right: { style: 'thin', color: { argb: 'CCCCCC' } },
                };
                
                // Add colored background for cells with unavailable employees
                for (let colIdx = 2; colIdx <= workPositionRow.length; colIdx++) {
                    const cell = addedRow.getCell(colIdx);
                    const cellValue = cell.value as string;
                    
                    if (cellValue?.includes('🚫')) {
                        // Add a light red background for unavailable employees
                        cell.fill = {
                            type: 'pattern',
                            pattern: 'solid',
                            fgColor: { argb: 'ffebee' }, // Light red background
                        };
                        // Set red text color for unavailable employees
                        if (cell.font) {
                            cell.font.color = { argb: 'd32f2f' }; // Dark red text
                        }
                    }
                }
                
                currentRow++;
            }
            
            // Empty row between weeks
            worksheet.addRow([]);
            currentRow++;
        }

        // Auto-fit columns
        worksheet.columns.forEach(column => {
            column.width = Math.max(25, column.width || 25);
        });
    }
};

const createReport = async (
    departments: DepartmentEntity[],
    startDate: Date,
    endDate: Date,
    seasonId: number,
    allDepartments: boolean,
    selectedDepartments?: string[],
    selectedWorkPositions?: string[]
) => {
    const dates = getDates(startDate, endDate);
    const workbook = createWorkbook();

    // Add compact shift overview tab (first tab)
    await addCompactShiftOverviewTab(workbook, startDate, endDate, seasonId, selectedDepartments, selectedWorkPositions);

    // Filter departments based on selection
    let filteredDepartments = departments;
    if (selectedDepartments && selectedDepartments.length > 0) {
        filteredDepartments = departments.filter(dept => selectedDepartments.includes(dept.id.toString()));
    } else if (!allDepartments) {
        // If no specific departments selected and not all departments, use only the first one
        filteredDepartments = [departments[0]];
    }

    // Department ids that scope the Worked Hours Summary (hours + absences). When "all"
    // is selected we pass every department so the summary aggregates them all.
    const summaryDepartmentIds = (allDepartments && (!selectedDepartments || selectedDepartments.length === 0))
        ? departments.map(d => d.id)
        : filteredDepartments.filter(Boolean).map(d => d.id);

    if (allDepartments || selectedDepartments) {
        // Add worked hours summary scoped to the selected departments (second tab)
        await addWorkedHoursSummaryTab(workbook, startDate, endDate, seasonId, summaryDepartmentIds);
        // Add a shift report tab for each filtered department
        for (const department of filteredDepartments) {
            await addDepartmentTable(workbook, department, seasonId, dates, selectedWorkPositions);
        }
    } else {
        // Only use the first department (current department)
        const department = departments[0];
        await addWorkedHoursSummaryTab(workbook, startDate, endDate, seasonId, summaryDepartmentIds);
        await addDepartmentTable(workbook, department, seasonId, dates, selectedWorkPositions);
    }
    return workbook;
}

export const shiftsReport = async (
    startDate: Date,
    endDate: Date,
    seasonId: number,
    allDepartments: boolean,
    selectedDepartments?: string[],
    selectedWorkPositions?: string[]
) => {
    const role = checkTokenRole();
    const departments = role === "DEPCHIEF" ?
        (await departmentsApi.findWhereIsBoss(seasonId)).data :
        (await departmentsApi.findAll()).data;
    const workbook = await createReport(
        departments,
        startDate,
        endDate,
        seasonId,
        allDepartments,
        selectedDepartments,
        selectedWorkPositions
    );
    downloadReport(workbook);
}

